/**
 * User Role Dashboard Mapping
 * Maps usernames to their corresponding dashboard routes
 */

// Map of usernames to dashboard routes
export const userRoleDashboards = {
    // Director/Management roles
    companyDirector: '/dashboard',     // Main dashboard with overview
    
    // Finance roles
    financeManager: '/dashboard/finance',
    financeUser: '/dashboard/finance',
    financePerson: '/dashboard/finance',
    
    // Project roles
    projectManager: '/dashboard',
    
    // Asset management roles
    assetsManager: '/assets/equipment',
    
    // Crewing roles
    crewBooker: '/dashboard/crewing',
    crewChief: '/dashboard/crewing',
    crew: '/dashboard/crew',
    
    // Production/Shooting roles
    producer: '/dashboard/shooting',
    director: '/dashboard/shooting',      // Film/Production director
    cinematographer: '/dashboard/shooting',
    productionAssistant: '/dashboard/shooting',
    
    // Logistics & Transport roles
    transportManager: '/dashboard/logistics',
    driver: '/dashboard/logistics',
    
    // Workshop roles
    workshop: '/dashboard/workshop',
    workshopManager: '/dashboard/workshop',
    
    // Warehouse roles
    warehouseManager: '/dashboard/warehouse',
    warehouseTransport: '/dashboard/warehouse',
    warehouseHOD: '/dashboard/warehouse',
    warehouseUser: '/dashboard/warehouse',
    
    // Freelancer (no company; works for many)
    freelance_user: '/dashboard/freelance',

    // Company accounts (tenant staff)
    company_admin: '/dashboard',
    company_user: '/dashboard',

    // Default dashboard for other users
    default: '/dashboard'
};

/**
 * Legacy `user.role.name` or multi-role `user.roles` (primary pivot first).
 * @param {Object|null} user
 * @returns {string|null}
 */
export const getPrimaryRoleName = (user) => {
    if (!user) return null;
    if (user.role?.name) return user.role.name;
    if (!Array.isArray(user.roles) || user.roles.length === 0) return null;
    const primary = user.roles.find((r) => r?.pivot?.is_primary || r?.is_primary);
    const r = primary ?? user.roles[0];
    return typeof r === 'string' ? r : r?.name ?? null;
};

/**
 * Get dashboard route based on user role (preferred for role-based redirects)
 * @param {Object} user - User object from auth
 * @returns {string|null} - Dashboard route or null if no role-based route
 */
export const getDashboardByRole = (user) => {
    const roleName = getPrimaryRoleName(user);
    if (!roleName) return null;
    const route = userRoleDashboards[roleName];
    return route ?? null;
};

/**
 * Get dashboard route based on username
 * @param {string} username - The username to check
 * @returns {string} - The dashboard route for this user
 */
export const getDashboardByUsername = (username) => {
    if (!username) return userRoleDashboards.default;
    
    // Convert to lowercase for case-insensitive matching
    const lowerUsername = username.toLowerCase();
    
    // Check for exact matches first
    for (const [key, route] of Object.entries(userRoleDashboards)) {
        if (lowerUsername === key.toLowerCase()) {
            return route;
        }
    }
    
    // Check for partial matches if no exact match found
    for (const [key, route] of Object.entries(userRoleDashboards)) {
        if (lowerUsername.includes(key.toLowerCase())) {
            return route;
        }
    }
    
    // Return default route if no match found
    return userRoleDashboards.default;
}; 